// Tap to talk in the kadmon panel — T57 (10 Sep 2026), agents doc § 7.4. The engines are
// src/speech.ts; this is the React half: which engines this build has, and the tap.
//
// WHICH ENGINES. A context, filled once at boot. src/main.tsx gives it Amazon Transcribe when
// VITE_VOICE_INPUT says `transcribe` — the deployed brain's since T57b, the engine Natanel chose
// for real candidates (10 Sep 2026) — the browser's own when it says `browser`, and nothing
// otherwise. harness/main.tsx gives it the browser's for `console:serve` and a scripted pair for
// the phone check. No engine, no mic.
//
// THE TAP (§ 7.4, Natanel's answer 4). Tap to start; silence or a second tap ends it. What was
// heard lands in the composer as text and NOTHING is asked until the recruiter presses Ask — so a
// misheard name or digit is seen, and corrected, before it reaches kadmon, the chat every recruiter
// reads, or the log. The answer to a dictated question is read aloud; a typed one is not. In tap
// to talk the confirm card is tapped, never spoken: nothing in this hook can press it, and the
// engine listens between two taps, never past a minute.
//
// VOICE MODE (T66a, 13 Sep 2026) is the other way in: `useVoiceMode`, a hands-free loop that lives
// beside the engines (src/voice-mode.ts) — she listens, the recruiter stops, she answers in her
// voice, she listens again; a mute, a tap to interrupt, and a spoken yes on a card, at Natanel's
// word. The panel draws it as a stage over the chat (`VoiceStage`, since 17 Sep 2026; a bubble at
// its foot until then) and opens it with `TalkButton`.
//
// WHY WHETHER kadmon IS READING LIVES IN THE CONTEXT, not in the panel. A new chat learns its id from
// the answer to its first question, the URL takes the id, and the panel — keyed on the chat —
// remounts, while that answer is being read aloud. So the reading is kept beside the engine, where
// the remounted panel finds it and still offers Stop.
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import type { DictationResponse } from '@vera/bff';
import {
  appendHeard,
  browserPollyParts,
  browserSpeechInput,
  browserSpeechOutput,
  explainSpeechError,
  pollySpeechOutput,
  spokenText,
  type Listening,
  type SpeechInput,
  type SpeechOutput,
  type SpeechSink,
} from '../speech.ts';
import type { FinishedAsk } from '../silence.ts';
import { useMe } from '../api/me.ts';
import { createRealtimeVoice, OPENAI_HEARS, type RealtimeParts } from '../realtime.ts';
import { createElevenLabsVoice, ELEVENLABS_HEARS, type ElevenLabsParts } from '../elevenlabs.ts';
import { browserSocket, transcribeSpeechInput, webAudioCapture } from '../transcribe.ts';
import type { OrbLevels } from '../orb.ts';
import { Button } from '../ui/atoms.tsx';
import {
  createVoiceMode,
  saidLine,
  type VoiceCallbacks,
  type VoiceModeStore,
  type VoiceSnapshot,
} from '../voice-mode.ts';
import { heldFlip, shownLayout, type StageFlip, type StageLayout } from '../voice-stage.ts';
import { voiceWhoLine } from '../voice-who.ts';
import { VoiceOrb } from './VoiceOrb.tsx';

/** The engines a build can have. Any may be missing from the browser in front of us. */
export interface Voice {
  input: SpeechInput | null;
  output: SpeechOutput | null;
  /** The conversation on OpenAI's voice model (T67): the key's source and the WebRTC call. */
  realtime?: RealtimeParts | null;
  /** The conversation on ElevenLabs' agent (23 Sep 2026): the token's source and the call. */
  elevenlabs?: ElevenLabsParts | null;
}

/** Her voice (T66): one sentence's text to the brain API, its audio back (api/speech.ts `say`). */
export type Say = (text: string) => Promise<ArrayBuffer>;

/**
 * The voice the answer is read in: Polly through the brain API when this build says so
 * (`VITE_VOICE_OUTPUT=polly`, the deployed brain's since T66), else the device's own; null
 * where the browser has neither.
 */
function outputFor(scope: object, say: Say | null): SpeechOutput | null {
  const parts = say ? browserPollyParts(scope, say) : null;
  return parts ? pollySpeechOutput(parts) : browserSpeechOutput(scope);
}

/**
 * The browser's own recognition (its vendor hears the audio), for local trials on invented data;
 * the answer in Polly's voice when `say` is given, else the device's.
 */
export function browserVoice(scope: object, say: Say | null = null, finished?: FinishedAsk): Voice {
  return { input: browserSpeechInput(scope, finished), output: outputFor(scope, say) };
}

/**
 * Amazon Transcribe for the words (T57b) — the URL the BFF signs per dictation, the browser's
 * microphone and socket — and Polly's voice for the answer when `say` is given (T66), else the
 * device's. No mic where the browser lacks Web Audio's worklet or a WebSocket.
 */
export function transcribeVoice(
  scope: object,
  session: () => Promise<DictationResponse>,
  say: Say | null = null,
  realtime: RealtimeParts | null = null,
  finished?: FinishedAsk,
): Voice {
  const capture = webAudioCapture(scope);
  const socket = browserSocket(scope);
  return {
    input:
      capture && socket
        ? transcribeSpeechInput({ session, capture, socket, ...(finished ? { finished } : {}) })
        : null,
    output: outputFor(scope, say),
    realtime,
  };
}

/** The output with one fact beside it — whether it is reading — for a panel that remounts. */
interface Reader {
  /** Read `text`; `done` when it has been read, or never when the reading was stopped. */
  speak(text: string, done?: () => void): void;
  /** Read an answer as it is written (T66b); each push is rewritten for the ear like a speak. */
  stream(done: () => void): SpeechSink;
  cancel(): void;
  prime(): void;
  speaking(): boolean;
  subscribe(listener: () => void): () => void;
}

function readerFor(output: SpeechOutput): Reader {
  let speaking = false;
  let turn = 0;
  const listeners = new Set<() => void>();
  const set = (next: boolean) => {
    if (speaking === next) return;
    speaking = next;
    for (const listener of listeners) listener();
  };
  return {
    speak(text, done) {
      turn += 1;
      const mine = turn;
      // Said as a voice should say it (T66): no slash, no link, a number a digit at a time. The
      // screen keeps the answer as written.
      const spoken = spokenText(text);
      set(true);
      output.speak(spoken, () => {
        if (mine !== turn) return;
        set(false);
        done?.();
      });
    },
    stream(done) {
      turn += 1;
      const mine = turn;
      set(true);
      const sink = output.stream(() => {
        if (mine !== turn) return;
        set(false);
        done();
      });
      return {
        push(text) {
          if (mine === turn) sink.push(spokenText(text));
        },
        end() {
          if (mine === turn) sink.end();
        },
      };
    },
    cancel() {
      turn += 1;
      output.cancel();
      set(false);
    },
    prime: () => output.prime(),
    speaking: () => speaking,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

interface Engines {
  input: SpeechInput | null;
  reader: Reader | null;
  /** Voice mode's local loop (T66a): only with both an input and a reader. */
  mode: VoiceModeStore | null;
  /** Voice mode on OpenAI's voice model (T67): only where the build and the browser have it. */
  realtime: VoiceModeStore | null;
  /** Voice mode on ElevenLabs' agent (23 Sep 2026): only where the browser can hold the call. */
  elevenlabs: VoiceModeStore | null;
}

const VoiceContext = createContext<Engines | null>(null);

export function VoiceProvider({ voice, children }: { voice: Voice | null; children: ReactNode }) {
  const engines = useMemo<Engines | null>(() => {
    if (!voice) return null;
    const reader = voice.output ? readerFor(voice.output) : null;
    const mode = voice.input && reader ? createVoiceMode(voice.input, reader) : null;
    const realtime = voice.realtime ? createRealtimeVoice(voice.realtime) : null;
    const elevenlabs = voice.elevenlabs ? createElevenLabsVoice(voice.elevenlabs) : null;
    return { input: voice.input, reader, mode, realtime, elevenlabs };
  }, [voice]);
  return <VoiceContext.Provider value={engines}>{children}</VoiceContext.Provider>;
}

const noSubscription = () => () => {};
const notSpeaking = () => false;

/** Stops the reading, and voice mode, when the panel closes — by ×, by Escape, or by Back. */
export function useQuietWhenClosed(open: boolean): void {
  const engines = useContext(VoiceContext);
  const reader = engines?.reader ?? null;
  const mode = engines?.mode ?? null;
  const realtime = engines?.realtime ?? null;
  const elevenlabs = engines?.elevenlabs ?? null;
  useEffect(() => {
    if (!open) {
      mode?.end();
      realtime?.end();
      elevenlabs?.end();
      reader?.cancel();
    }
  }, [open, reader, mode, realtime, elevenlabs]);
}

export type TalkMode = 'idle' | 'listening' | 'speaking';

export interface TapToTalk {
  /** Whether to draw a mic at all: an engine in this build, and in this browser. */
  available: boolean;
  mode: TalkMode;
  /** The line under the composer: listening, what to do with what was heard, or why nothing was. */
  note: { text: string; error: boolean } | null;
  /** The button. Idle: listen. Listening: stop, with what was heard. Reading: stop reading. */
  press(): void;
  /** The question is going: stop reading the last answer, and say whether this one was dictated. */
  beginAsk(): boolean;
  /** Read kadmon's answer aloud. */
  speak(text: string): void;
  /** Stop reading — another chat was picked, or a new one started. */
  hush(): void;
}

/** The line under the composer while the mic is open, with who hears it — said every time. */
const listeningNote = (whoHears: string) =>
  `Listening. Pause, or tap Stop, when you are done. ${whoHears}.`;
const HEARD =
  'That is what was heard. Put right anything misheard, then Ask. Nothing is sent until you do.';

export function useTapToTalk({
  draft,
  setDraft,
  onHeard,
}: {
  draft: string;
  setDraft: (text: string) => void;
  /** What was heard is in the composer. */
  onHeard: () => void;
}): TapToTalk {
  const engines = useContext(VoiceContext);
  const input = engines?.input ?? null;
  const reader = engines?.reader ?? null;
  const speaking = useSyncExternalStore(
    reader ? reader.subscribe : noSubscription,
    reader ? reader.speaking : notSpeaking,
  );
  const [listening, setListening] = useState(false);
  const [note, setNote] = useState<TapToTalk['note']>(null);
  const session = useRef<Listening | null>(null);
  const dictated = useRef(false);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // A composer emptied by hand no longer holds the dictated question.
  useEffect(() => {
    if (draft.trim() === '') dictated.current = false;
  }, [draft]);

  // The panel going away — closed, or another chat picked — takes an open mic with it.
  useEffect(() => () => session.current?.abort(), []);

  const listen = () => {
    if (!input) return;
    reader?.prime();
    const before = draftRef.current;
    setNote({ text: listeningNote(input.whoHears), error: false });
    setListening(true);
    session.current = input.listen({
      onText: (text) => setDraft(appendHeard(before, text)),
      onEnd: (heard) => {
        session.current = null;
        setListening(false);
        if ('text' in heard) {
          setDraft(appendHeard(before, heard.text));
          dictated.current = true;
          setNote({ text: HEARD, error: false });
          onHeard();
        } else {
          setDraft(before);
          const said = explainSpeechError(heard.error);
          setNote(said === null ? null : { text: said, error: true });
        }
      },
    });
  };

  return {
    available: input !== null,
    mode: listening ? 'listening' : speaking ? 'speaking' : 'idle',
    note,
    press() {
      if (listening) session.current?.stop();
      else if (speaking) reader?.cancel();
      else listen();
    },
    beginAsk() {
      const was = dictated.current;
      dictated.current = false;
      setNote(null);
      reader?.cancel();
      return was;
    },
    speak(text) {
      reader?.speak(text);
    },
    hush() {
      engines?.mode?.end();
      engines?.realtime?.end();
      engines?.elevenlabs?.end();
      reader?.cancel();
    },
  };
}

/** Voice mode as the panel draws it (T66a): the loop's state, and the four things a tap does. */
export interface VoiceMode extends VoiceSnapshot {
  /** Whether to draw Talk at all: an input and a voice in this build, and in this browser. */
  available: boolean;
  /** Who hears the recruiter, for the stage's line — the same words the mic's note says. */
  whoHears: string;
  start(): void;
  end(): void;
  toggleMute(): void;
  interrupt(): void;
  /** How loud the recruiter and kadmon are this instant, for the orb; null with no reading. */
  levels(): OrbLevels | null;
}

const OFF: VoiceSnapshot = {
  state: 'off',
  heard: '',
  said: '',
  muted: false,
  note: null,
  card: null,
  showing: false,
};
const offSnapshot = () => OFF;

/**
 * The hands-free loop, bound to this panel's ask, confirm and dismiss. The callbacks are rebound
 * on every render, so the panel a new chat's id remounts is the one whose ask the loop calls next.
 */
export function useVoiceMode(callbacks: VoiceCallbacks): VoiceMode {
  const engines = useContext(VoiceContext);
  // T67: the conversation runs on OpenAI's voice model where this server has a key (`/me` says
  // so) and the build and the browser can open the call; the local loop is the fallback, and the
  // phone check's. Since 23 Sep 2026 on ElevenLabs' agent instead where the server is set up for
  // it (`voiceVendor`) — it needs no engine of this build's, only a browser that can hold a call.
  const me = useMe();
  const vendor = me.data?.voiceEnabled === true ? (me.data.voiceVendor ?? 'openai') : null;
  const onElevenLabs = vendor === 'elevenlabs' && engines?.elevenlabs != null;
  const onOpenAi = vendor === 'openai' && engines?.realtime != null;
  const mode = onElevenLabs
    ? (engines?.elevenlabs ?? null)
    : onOpenAi
      ? (engines?.realtime ?? null)
      : (engines?.mode ?? null);
  mode?.bind(callbacks);
  const snapshot = useSyncExternalStore(
    mode ? mode.subscribe : noSubscription,
    mode ? mode.snapshot : offSnapshot,
  );
  // 16 Sep 2026: while the panel is open and voice mode is not, Talk is got ready — on OpenAI, its
  // key minted and its connection warmed (realtime.ts `prepare`); since 23 Sep 2026 on ElevenLabs
  // too, its token minted and its library and connection warmed (elevenlabs.ts `prepare`); again
  // after each conversation. Since 24 Sep 2026 kept ready — the token replaced before it goes
  // stale — until the panel closes or a conversation begins (`unprepare`).
  useEffect(() => {
    if (snapshot.state !== 'off') return;
    mode?.prepare?.();
    return () => mode?.unprepare?.();
  }, [mode, snapshot.state]);
  return {
    ...snapshot,
    available: mode !== null,
    whoHears: onElevenLabs
      ? ELEVENLABS_HEARS
      : onOpenAi
        ? OPENAI_HEARS
        : (engines?.input?.whoHears ?? ''),
    start: () => mode?.start(),
    end: () => mode?.end(),
    toggleMute: () => mode?.toggleMute(),
    interrupt: () => mode?.interrupt(),
    levels: () => mode?.levels?.() ?? null,
  };
}

/** Opens voice mode, beside the mic. Nothing at all where the build has no input or no voice. */
export function TalkButton({ voice, disabled }: { voice: VoiceMode; disabled: boolean }) {
  if (!voice.available || voice.state !== 'off') return null;
  return (
    <span className="talk">
      <Button
        kind="ghost"
        icon="phone"
        // 24 Sep 2026 (Natanel: "an almost instant connection"): a mouse's press starts the call,
        // so the tenth of a second before the click lands is spent connecting. A touch or a key
        // starts it on the click, as before — a phone's audio may start only on a touch's end.
        onPointerDown={(event) => {
          // A disabled button can still be pressed in some browsers, which fire pointer events
          // on disabled controls where they fire no click.
          if (!disabled && event.pointerType === 'mouse' && event.button === 0) voice.start();
        }}
        onClick={voice.start}
        disabled={disabled}
        aria-label="Talk with kadmon"
        title="Talk with kadmon: she listens, answers in her voice, and listens again"
      >
        Talk
      </Button>
    </span>
  );
}

/** The stage's line for each state, in the brain's words. */
export function voiceStateLine(voice: VoiceSnapshot): string {
  switch (voice.state) {
    case 'connecting':
      return 'Connecting…';
    case 'listening':
      return 'Listening. Pause when you are done.';
    case 'thinking':
      return 'Thinking…';
    case 'speaking':
      return 'Speaking. Tap Interrupt to talk over her.';
    case 'muted':
      return 'Muted. Nothing is heard until you unmute.';
    default:
      return '';
  }
}

/**
 * Where the orb sits in the panel (voice-stage.ts, 17 Sep 2026): big over the chat, or at its top
 * while she speaks about what she put on screen and while a card waits for a tap — and the tap on
 * the orb that swaps the two until the state next changes.
 */
export function useVoiceStage(voice: VoiceMode): { layout: StageLayout; swap(): void } {
  const [flip, setFlip] = useState<StageFlip | null>(null);
  const facts = { state: voice.state, card: voice.card, showing: voice.showing };
  // A tap is let go once what is on screen has changed (voice-stage.ts), here in render as React
  // adjusts state to a changed value — never on her speaking or listening, which flicker.
  const held = heldFlip(flip, facts);
  if (held !== flip) setFlip(held);
  const layout = shownLayout(facts, held);
  return {
    layout,
    swap: () =>
      setFlip({
        card: voice.card,
        showing: voice.showing,
        layout: layout === 'centre' ? 'docked' : 'centre',
      }),
  };
}

/**
 * Voice mode in the chat (T66a): the orb, what is happening, what is being heard, and the taps —
 * Interrupt while she speaks, Mute, End. Nothing where the mode is off. Since 16 Sep 2026 the orb is
 * a glass bubble that hears and speaks (`VoiceOrb`, src/orb.ts); since 17 Sep 2026 it is a stage over
 * the chat rather than a bubble at its foot: big in the middle, as ChatGPT's voice screen is, and at
 * the top whenever the chat has something she wants seen (`useVoiceStage`). What is said moves with
 * it: faded out while the orb travels, back in its new place.
 */
export function VoiceStage({
  voice,
  layout,
  onSwap,
}: {
  voice: VoiceMode;
  layout: StageLayout;
  onSwap: () => void;
}) {
  const [moving, setMoving] = useState(false);
  const shown = useRef(layout);
  useEffect(() => {
    if (shown.current === layout) return;
    shown.current = layout;
    setMoving(true);
    const settle = window.setTimeout(() => setMoving(false), 380);
    return () => window.clearTimeout(settle);
  }, [layout]);
  if (voice.state === 'off') return null;
  return (
    <div
      className={`voice voice--${voice.state}`}
      data-layout={layout}
      data-moving={moving ? '' : undefined}
      role="status"
      aria-live="polite"
    >
      <button
        type="button"
        className="voice__orb-button"
        onClick={onSwap}
        aria-label={layout === 'centre' ? 'Move kadmon to the top' : 'Bring kadmon to the middle'}
      >
        <VoiceOrb state={voice.state} levels={voice.levels} />
      </button>
      <div className="voice__lines">
        <div className="voice__state">{voiceStateLine(voice)}</div>
        {voice.heard !== '' && <div className="voice__heard">{voice.heard}</div>}
        {voice.said !== '' && <div className="voice__said">{saidLine(voice.said)}</div>}
      </div>
      <div className="voice__actions">
        {voice.state === 'speaking' && (
          <Button kind="ghost" size="sm" icon="pause" onClick={voice.interrupt}>
            Interrupt
          </Button>
        )}
        <Button
          kind={voice.muted ? 'accent' : 'ghost'}
          size="sm"
          icon="mic"
          onClick={voice.toggleMute}
          aria-pressed={voice.muted}
        >
          {voice.muted ? 'Unmute' : 'Mute'}
        </Button>
        <Button kind="quiet" size="sm" icon="x" onClick={voice.end}>
          End
        </Button>
      </div>
      <p className="micro voice__who">{voiceWhoLine(voice.whoHears)}</p>
    </div>
  );
}

/** The mic, between the composer and Ask. Nothing at all where there is no engine. */
export function MicButton({ talk, disabled }: { talk: TapToTalk; disabled: boolean }) {
  if (!talk.available) return null;
  const label =
    talk.mode === 'listening'
      ? 'Stop listening'
      : talk.mode === 'speaking'
        ? 'Stop reading the answer aloud'
        : 'Speak to kadmon';
  return (
    <span className={`mic mic--${talk.mode}`}>
      <Button
        kind={talk.mode === 'listening' ? 'accent' : 'ghost'}
        icon={talk.mode === 'speaking' ? 'x' : 'mic'}
        onClick={talk.press}
        disabled={disabled && talk.mode === 'idle'}
        aria-label={label}
        title={label}
      >
        {talk.mode === 'idle' ? null : 'Stop'}
      </Button>
    </span>
  );
}
